import React, { useEffect, useState } from 'react';
import { CheckCircle, Calendar, Clock, User, Stethoscope, Sparkles, MapPin, Phone, Mail, Download, Plus, Home } from 'lucide-react';
import Button from '../ui/Button';

const UnifiedBookingSuccess = ({ bookingData, bookingType = 'therapy' }) => {
  const [showConfetti, setShowConfetti] = useState(true);
  const [bookingId] = useState(bookingData?.appointmentId?.slice(-8)?.toUpperCase() || `APT${Date.now()?.toString()?.slice(-6)}`);

  // Track Google Ads conversion on component mount
  useEffect(() => {
    // Track conversion for Google Ads
    if (window.gtag && bookingData) {
      const conversionValue = bookingData?.payment?.amount || bookingData?.pricing?.total || 450;

      // Track the conversion event
      window.gtag('event', 'conversion', {
        'send_to': 'AW-16568972495', // Base conversion tracking
        'value': conversionValue,
        'currency': 'INR',
        'transaction_id': bookingData?.appointmentId || bookingId
      });

      // Also track as a custom event for better analytics
      window.gtag('event', 'appointment_booking', {
        'event_category': 'Booking',
        'event_label': bookingType === 'consultation' ? 'Doctor Consultation' : 'Therapy Appointment',
        'value': conversionValue,
        'currency': 'INR',
        'booking_type': bookingType,
        'payment_status': bookingData?.payment?.status || 'pending'
      });

      console.log('✅ Google Ads conversion tracked:', {
        type: bookingType,
        value: conversionValue,
        transactionId: bookingData?.appointmentId || bookingId
      });
    } else if (!window.gtag) {
      console.warn('⚠️ Google Ads gtag not loaded - conversion not tracked');
    }
  }, [bookingData, bookingType, bookingId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Format date and time from bookingData
  const formatDateTime = () => {
    if (!bookingData?.dateTime?.date || !bookingData?.dateTime?.timeSlot) {
      return 'Date and time to be confirmed';
    }

    const date = new Date(bookingData.dateTime.date);
    const formattedDate = date?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Format time slot (e.g., "14:00" to "2:00 PM")
    const timeSlot = bookingData.dateTime.timeSlot;
    const [hours, minutes] = timeSlot.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const formattedTime = `${displayHour}:${minutes} ${ampm}`;

    return `${formattedDate} at ${formattedTime}`;
  };

  // Get pricing from payment data (already calculated with discount and GST)
  const getPricing = () => {
    if (bookingData?.payment) {
      return {
        basePrice: bookingData.payment.basePrice || 0,
        gstAmount: bookingData.payment.gstAmount || 0,
        discountAmount: bookingData.payment.discountAmount || 0,
        discountReason: bookingData.payment.discountReason || null,
        total: bookingData.payment.amount || 0,
        hasGst: (bookingData.payment.gstAmount || 0) > 0,
        hasDiscount: (bookingData.payment.discountAmount || 0) > 0
      };
    }

    // Fallback if payment data not available
    const basePrice = bookingType === 'consultation'
      ? (bookingData?.doctor?.consultationFee || 500)
      : (bookingData?.services?.reduce((sum, service) => sum + (service?.base_price || 0), 0) || 0);

    return {
      basePrice,
      gstAmount: 0,
      discountAmount: 0,
      discountReason: null,
      total: basePrice,
      hasGst: false,
      hasDiscount: false
    };
  };

  const pricing = getPricing();

  const handleAddToCalendar = () => {
    try {
      console.log('🗓️ Calendar Event Debug - Full bookingData:', bookingData);
      console.log('🗓️ Booking Type:', bookingType);

      // Parse the date and time
      let dateInput = bookingData?.dateTime?.date;
      const timeStr = bookingData?.dateTime?.timeSlot;

      console.log('🗓️ Date Input (raw):', dateInput);
      console.log('🗓️ Date Input Type:', typeof dateInput);
      console.log('🗓️ Time String:', timeStr);

      if (!dateInput || !timeStr) {
        console.error('❌ Missing date/time data:', { dateInput, timeStr, bookingData });
        alert('Date and time information is missing. Please contact support.');
        return;
      }

      // Convert Date object to YYYY-MM-DD string if needed
      let dateStr;
      if (dateInput instanceof Date) {
        // Date object - convert to YYYY-MM-DD
        const year = dateInput.getFullYear();
        const month = String(dateInput.getMonth() + 1).padStart(2, '0');
        const day = String(dateInput.getDate()).padStart(2, '0');
        dateStr = `${year}-${month}-${day}`;
        console.log('🗓️ Converted Date object to string:', dateStr);
      } else if (typeof dateInput === 'string') {
        // Already a string - use as is
        dateStr = dateInput;
        console.log('🗓️ Date is already a string:', dateStr);
      } else {
        console.error('❌ Invalid date type:', typeof dateInput);
        alert('Invalid date format. Please contact support.');
        return;
      }

      // Validate date format (should be YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(dateStr)) {
        console.error('❌ Invalid date format:', dateStr);
        alert('Invalid date format. Please contact support.');
        return;
      }

      // Validate time format (should be HH:MM)
      const timeRegex = /^\d{2}:\d{2}$/;
      if (!timeRegex.test(timeStr)) {
        console.error('❌ Invalid time format:', timeStr);
        alert('Invalid time format. Please contact support.');
        return;
      }

      // Create start date
      const [year, month, day] = dateStr.split('-');
      const [hours, minutes] = timeStr.split(':');
      const startDate = new Date(year, month - 1, day, hours, minutes);

      console.log('🗓️ Start Date Object:', startDate);
      console.log('🗓️ Start Date Valid?', !isNaN(startDate.getTime()));

      if (isNaN(startDate.getTime())) {
        console.error('❌ Invalid date created:', { year, month, day, hours, minutes });
        alert('Failed to create calendar event. Invalid date/time.');
        return;
      }

      // Create end date (1 hour later for consultation, service duration for therapy)
      const endDate = new Date(startDate);
      if (bookingType === 'consultation') {
        endDate.setHours(endDate.getHours() + 1);
      } else {
        // Calculate total duration from services
        const totalDuration = bookingData?.services?.reduce((sum, service) => sum + (service?.duration_minutes || 45), 0) || 45;
        console.log('🗓️ Total Duration (minutes):', totalDuration);
        endDate.setMinutes(endDate.getMinutes() + totalDuration);
      }

      console.log('🗓️ End Date Object:', endDate);

      // Format dates for Google Calendar (YYYYMMDDTHHMMSSZ)
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}${month}${day}T${hours}${minutes}00`;
      };

      const formattedStart = formatDate(startDate);
      const formattedEnd = formatDate(endDate);

      console.log('🗓️ Formatted Start:', formattedStart);
      console.log('🗓️ Formatted End:', formattedEnd);

      let calendarUrl;
      if (bookingType === 'consultation') {
        const doctorName = bookingData?.doctor?.fullName || 'Doctor';
        const treatmentName = bookingData?.treatmentName || 'Consultation';
        calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent('Doctor Consultation - ' + doctorName)}&dates=${formattedStart}/${formattedEnd}&details=${encodeURIComponent('Consultation for ' + treatmentName + '\nDoctor: Dr. ' + doctorName + '\nFee: ₹' + pricing.total)}&location=${encodeURIComponent('1st Floor, LAKSHMI NARAYANA ESTATES, High Tension Lane, Nadigada Tanda, Miyapur, Hyderabad, Telangana 500049')}`;
      } else {
        const serviceNames = bookingData?.services?.map(s => s.name).join(', ') || 'Therapy Session';
        calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent('Therapy Appointment')}&dates=${formattedStart}/${formattedEnd}&details=${encodeURIComponent('Services: ' + serviceNames + '\nTotal: ₹' + pricing.total)}&location=${encodeURIComponent('1st Floor, LAKSHMI NARAYANA ESTATES, High Tension Lane, Nadigada Tanda, Miyapur, Hyderabad, Telangana 500049')}`;
      }

      console.log('🗓️ Calendar URL:', calendarUrl);
      window.open(calendarUrl, '_blank');
    } catch (error) {
      console.error('❌ Error creating calendar event:', error);
      console.error('❌ Error stack:', error.stack);
      alert('Failed to create calendar event. Please add it manually.');
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleNewBooking = () => {
    window.location.href = bookingType === 'consultation' ? '/specialist-booking' : '/booking';
  };

  const handleGoHome = () => {
    window.location.href = '/homepage';
  };

  const getPaymentStatusColor = () => {
    const status = bookingData?.payment?.status;
    if (status === 'paid') return 'text-green-600 bg-green-50';
    if (status === 'pending') return 'text-amber-600 bg-amber-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getPaymentStatusText = () => {
    const status = bookingData?.payment?.status;
    if (status === 'paid') return 'Paid';
    if (status === 'pending') return 'Pay at Clinic';
    return 'Pending';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="font-headline text-3xl font-bold text-gray-900 mb-2">
            Booking Confirmed!
          </h1>
          <p className="text-gray-600 text-lg">
            Your {bookingType === 'consultation' ? 'consultation' : 'therapy appointment'} has been successfully booked
          </p>
          <div className="mt-4 inline-block bg-purple-100 px-6 py-2 rounded-full">
            <p className="text-purple-800 font-semibold">
              Booking ID: <span className="font-mono">{bookingId}</span>
            </p>
          </div>
        </div>

        {/* Booking Details Card */}
        <div className="glass-panel p-8 rounded-2xl border border-purple-200 mb-6">
          <h2 className="font-semibold text-xl mb-6 text-purple-800">Appointment Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date & Time */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Date & Time</p>
                <p className="font-medium text-gray-900">{formatDateTime()}</p>
              </div>
            </div>

            {/* Client Info */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">{bookingType === 'consultation' ? 'Patient' : 'Client'}</p>
                <p className="font-medium text-gray-900">{bookingData?.details?.name}</p>
                <p className="text-sm text-gray-600">{bookingData?.details?.phone}</p>
              </div>
            </div>

            {/* Doctor (Consultation only) */}
            {bookingType === 'consultation' && bookingData?.doctor && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Doctor</p>
                  <p className="font-medium text-gray-900">Dr. {bookingData?.doctor?.fullName}</p>
                  {bookingData?.doctor?.specializations && bookingData?.doctor?.specializations?.length > 0 && (
                    <p className="text-sm text-gray-600">{bookingData?.doctor?.specializations?.[0]}</p>
                  )}
                </div>
              </div>
            )}

            {/* Services (Therapy only) */}
            {bookingType === 'therapy' && bookingData?.services && bookingData?.services?.length > 0 && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Services</p>
                  {bookingData?.services?.map((service, index) => (
                    <p key={index} className="font-medium text-gray-900">{service?.name}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Status */}
            <div className="flex items-start space-x-3">
              <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${getPaymentStatusColor()}`}>
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Payment Status</p>
                <p className="font-medium text-gray-900">{getPaymentStatusText()}</p>
                <p className="text-sm text-gray-600">₹{pricing.total?.toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* Pricing Breakdown */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-lg mb-4 text-gray-800">Payment Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{bookingType === 'consultation' ? 'Consultation Fee' : 'Base Price'}</span>
                <span className="text-gray-900">₹{pricing.basePrice?.toFixed(2)}</span>
              </div>

              {pricing.hasGst && (
                <div className="flex justify-between text-sm text-blue-600">
                  <span>GST</span>
                  <span>₹{pricing.gstAmount?.toFixed(2)}</span>
                </div>
              )}

              {pricing.hasDiscount && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount {pricing.discountReason && `(${pricing.discountReason})`}</span>
                  <span>-₹{pricing.discountAmount?.toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between text-lg font-semibold pt-2 border-t border-gray-200">
                <span className="text-gray-900">Total Amount</span>
                <span className="text-purple-600">₹{pricing.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Button
            onClick={handleAddToCalendar}
            variant="outline"
            className="w-full flex items-center justify-center space-x-2 border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            <Calendar className="w-5 h-5" />
            <span>Add to Calendar</span>
          </Button>

          <Button
            onClick={handlePrintReceipt}
            variant="outline"
            className="w-full flex items-center justify-center space-x-2 border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            <Download className="w-5 h-5" />
            <span>Download Receipt</span>
          </Button>

          <Button
            onClick={handleNewBooking}
            variant="outline"
            className="w-full flex items-center justify-center space-x-2 border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            <Plus className="w-5 h-5" />
            <span>New Booking</span>
          </Button>
        </div>

        {/* Clinic Information */}
        <div className="glass-panel p-6 rounded-2xl border border-purple-200 mb-6">
          <h3 className="font-semibold text-lg mb-4 text-purple-800">Clinic Information</h3>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Sri Dhanvantari Ayurvaidik Parishkar Kendra</p>
                <p className="text-sm text-gray-600">Miyapur, Hyderabad, Telangana</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-purple-600" />
              <p className="text-gray-900">+91 XXX XXX XXXX</p>
            </div>
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-purple-600" />
              <p className="text-gray-900">info@sdapk.com</p>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
          <h3 className="font-semibold text-lg mb-3 text-amber-900">Important Notes</h3>
          <ul className="space-y-2 text-sm text-amber-800">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Please arrive 15 minutes before your scheduled time</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Bring any previous medical records or prescriptions</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Wear comfortable clothing for {bookingType === 'consultation' ? 'examination' : 'therapy'}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>For cancellations or rescheduling, please contact us at least 24 hours in advance</span>
            </li>
            {bookingData?.payment?.status === 'pending' && (
              <li className="flex items-start font-semibold">
                <span className="mr-2">•</span>
                <span>Payment of ₹{pricing.total?.toFixed(2)} is due at the clinic</span>
              </li>
            )}
          </ul>
        </div>

        {/* Home Button */}
        <div className="text-center">
          <Button
            onClick={handleGoHome}
            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-semibold inline-flex items-center space-x-2"
          >
            <Home className="w-5 h-5" />
            <span>Back to Home</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UnifiedBookingSuccess;

